"use client";

import { useEffect } from "react";
import { getMessaging, onMessage } from "firebase/messaging";
import { getFirebaseApp } from "@/app/lib/firebase-client";
import { useFcmToken } from "@/context/fcmcontext";
import { saveFcmToken } from "@/app/api/service/firebase";
import { FCM } from "../interface/homepage";
import { useAuth } from "@/context/authcontext";

const VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

function validateVapidKey(key: string | undefined): boolean {
    if (!key) {
        console.error("❌ Missing VAPID key");
        return false;
    }

    try {
        let paddedKey = key;
        const pad = key.length % 4;
        if (pad) {
            paddedKey += "=".repeat(4 - pad);
        }

        const standardKey = paddedKey.replace(/-/g, "+").replace(/_/g, "/");
        atob(standardKey);
        return true;
    } catch (e) {
        console.error("❌ Invalid VAPID key format:", e);
        return false;
    }
}

export default function FCMInit({ setMessage }: { setMessage?: (message: FCM) => void }) {
    const { token } = useFcmToken(); // Triggers token refresh logic internally
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        if (!token) return;

        const setupFCM = async () => {
            try {
                if (!validateVapidKey(VAPID_KEY)) return;

                const app = getFirebaseApp();
                const messaging = getMessaging(app);

                if (isAuthenticated) {
                    await saveFcmToken(token);
                }

                onMessage(messaging, (payload) => {
                    if (payload.data?.type !== "pushNote") {
                        setMessage?.(payload.data as unknown as FCM);
                    } else {
                        setMessage?.({
                            ...payload.data,
                            title: payload.notification?.title || "Notification",
                            body: payload.notification?.body || "You have a new message.",
                            url: payload.data?.url || "https://www.incredmoney.com",
                        } as unknown as FCM);
                    }
                });
            } catch (error) {
                console.error("❌ Error setting up FCM:", error);
            }
        };

        setupFCM();
    }, [token, setMessage, isAuthenticated]);

    return null;
}